import React from "react";
import { useSelector } from "react-redux";

import { SINGLE_TRIP } from "@/constants";
import {
  ComparisonTableAreaContentType,
  ColumnTitlesType,
} from "@/schema/quote-page/page.schema";
import { RootState } from "@/store/root-reducer";
import isCruiseImpact from "@/utils/check-cruise-impact-base-on-brand.utils";

import ComparisionTableBlock from "./comparison-table-block";
import styles from "./comparison-table-cell.module.css";
import getComparisonTableData from "./magnolia-data-map-comparison-table";

type QuoteComparisionTableProps = {
  drawerTitle: string; // Change `any` to the appropriate type
  selectedTab: string; // Change `any` to the appropriate type
  drawerData: ComparisonTableAreaContentType; // Change `any` to the appropriate type
};

type TitleType =
  | "basic"
  | "comprehensive"
  | "signature"
  | "essentials"
  | "classic"
  | "deluxe";

export default function QuoteComparisionTable({
  drawerTitle,
  selectedTab,
  drawerData,
}: Readonly<QuoteComparisionTableProps>) {
  const quoteData = useSelector((state: RootState) => state.quote);
  const { attributes } = quoteData;

  // const [updatedVersion, setUpdatedVersion] = useState(version);
  const { tripDetails, priceResult } = attributes;

  const { comparisonTableAMTRow, comparisonTableSTRow, comparisonTableData } =
    drawerData;

  const compdata = getComparisonTableData(
    comparisonTableData,
    tripDetails?.goingOnCruise && isCruiseImpact,
    tripDetails?.policyType,
    priceResult?.policyPackagePrice
  );

  const renderTitle = (type: TitleType): string | null => {
    const titleMap: Record<TitleType, string> = {
      basic: "Basic",
      comprehensive: "Comprehensive",
      signature: "Signature",
      essentials: "Essentials",
      classic: "Classic",
      deluxe: "Deluxe",
    };

    return titleMap[type] || null;
  };

  const titles = compdata[0]?.columnTitle ?? {};

  const commonContainerClasses = "sm:h-[44px] grid";
  const commonSpanClasses =
    "justify-self-center self-center font-semibold-font sm:text-[14px] md:text-2xs font-semibold leading-[24px] tracking-normal text-center";

  const titleClasses: Record<string, string> = {
    basic: "md:w-[120px] sm:w-[96px] rounded-tl-radius-lg",
    essentials: "md:w-[120px] sm:w-[96px] rounded-tl-radius-lg",
    comprehensive: "md:w-[152px] sm:w-[120px]",
    classic: "md:w-[152px] sm:w-[120px]",
    signature: "md:w-[120px] sm:w-[96px] rounded-tr-radius-lg",
    deluxe: "md:w-[120px] sm:w-[96px] rounded-tr-radius-lg",
  };

  const renderColumns = (columnData: Record<string, ColumnTitlesType>) => {
    return Object.keys(columnData).map((key) => {
      const { title } = columnData[key];

      const containerClasses = `${commonContainerClasses} ${
        titleClasses[title] || ""
      } ${title} ${styles.backgroundColor}`;
      const spanClasses = commonSpanClasses;

      return (
        <div key={key} className={containerClasses}>
          <span className={spanClasses}>{renderTitle(title)}</span>
        </div>
      );
    });
  };

  const commonContainerClassesTwoColumn = "sm:h-[44px] grid";
  const commonSpanClassesTwoColumn =
    "justify-self-center self-center font-semibold-font sm:text-[14px] font-semibold leading-[21px] tracking-normal text-center";

  const twoColumnTitleClasses: Record<string, string> = {
    classic: "md:w-[196px] sm:w-[156px] rounded-tl-radius-lg",
    deluxe: "md:w-[196px] sm:w-[156px] rounded-tr-radius-lg",
    basic: "md:w-[196px] sm:w-[196px] rounded-tl-radius-lg",
    comprehensive: "md:w-[196px] sm:w-[196px] rounded-tr-radius-lg",
  };

  const renderTwoColumns = (columnData: Record<string, ColumnTitlesType>) => {
    return Object.keys(columnData).map((key) => {
      const { title } = columnData[key];

      const containerClasses = `${commonContainerClassesTwoColumn} ${
        twoColumnTitleClasses[title] || ""
      } ${title} ${styles.backgroundColor}`;
      const spanClasses = commonSpanClassesTwoColumn;

      return (
        <div key={key} className={containerClasses}>
          <span className={spanClasses}>{renderTitle(title)}</span>
        </div>
      );
    });
  };

  return (
    <div className="pt-[7px]" data-testid="quote-comparision-table-test-id">
      <div className="md:text-[31px] font-header-font md:leading-[34px] text-[26px] leading-[30px] tracking-normal text-left text-text-title mb-spacing-l">
        {drawerTitle}
      </div>
      <div className="flex flex-col items-center">
        <div className="flex-row flex md:w-[392px] sm:w-[312px] bg-transparent">
          {Object.keys(titles).length === 2
            ? renderTwoColumns(titles)
            : renderColumns(titles)}
        </div>
        <div className="bg-surface-neutral-option-1 md:w-[392px] sm:w-[312px] justify-self-center grid">
          <ComparisionTableBlock
            data={
              compdata[0]?.comparisonTableRow ??
              (selectedTab === SINGLE_TRIP
                ? comparisonTableSTRow
                : comparisonTableAMTRow)
            }
          />
        </div>
      </div>
    </div>
  );
}
